/** Choose the voice the platform speaks in.
 *
 * There are two quite different things behind this one control:
 *
 *   * Where the server has voice models, the choice is male or female, it
 *     is saved to the account, and it therefore follows the learner to
 *     every device. This is the normal case.
 *   * Where it does not, we are back to the browser's own synthesis, whose
 *     voices differ per machine. Then the control lists whatever THIS
 *     device has and remembers the pick locally.
 *
 * Renders nothing when there is nothing to choose between — a dropdown
 * with one option is just noise.
 */
import { useEffect, useState } from "react";
import { updateProfile } from "../../api/auth";
import * as speechApi from "../../api/speech";
import type { VoiceId } from "../../api/speech";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../components/ui";
import { useT } from "../../i18n";
import {
  getPreferredVoice,
  onVoicesReady,
  setPreferredVoice,
  speak,
  voicesFor,
} from "../../utils/tts";
import { speakBest } from "../../utils/voice";

/** A line in the target language, so the sample is representative. */
const SAMPLES: Record<string, string> = {
  de: "Guten Tag! Wie geht es Ihnen heute?",
  en: "Good afternoon! How are you doing today?",
  fr: "Bonjour ! Comment allez-vous aujourd'hui ?",
  es: "¡Buenos días! ¿Cómo está usted hoy?",
  it: "Buongiorno! Come sta oggi?",
};

const sampleFor = (lang: string) => SAMPLES[lang.slice(0, 2)] ?? SAMPLES.de;

export function VoicePicker({ lang }: { lang: string }) {
  const t = useT();
  const { user, refresh } = useAuth();
  const [server, setServer] = useState<speechApi.VoiceStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [browserVoices, setBrowserVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [localPick, setLocalPick] = useState<string>(() => getPreferredVoice() ?? "");

  useEffect(() => {
    let cancelled = false;
    speechApi
      .voiceStatus()
      .then((s) => {
        if (!cancelled) setServer(s);
      })
      // No status is the same as no server voice: fall back to the browser.
      .catch(() => {
        if (!cancelled) setServer({ configured: false, voices: [], preferred: "female", max_chars: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Chrome loads its voice list asynchronously — reading it once on mount
  // reliably finds nothing on a cold page.
  useEffect(() => onVoicesReady(() => setBrowserVoices(voicesFor(lang))), [lang]);

  if (server === null) return null; // still asking; don't flash a control

  // --- the normal case: our own voices, saved to the account -------------
  if (server.configured && server.voices.length > 1) {
    const chosen = (user?.voice_preference as VoiceId | null) ?? server.preferred;

    const choose = async (voice: VoiceId) => {
      setSaving(true);
      try {
        await updateProfile({ voice_preference: voice });
        await refresh();
      } catch {
        // Not worth an error toast: the sample below still plays in the
        // voice they picked, and they can try again.
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="mt-4">
        <label htmlFor="bot-voice" className="text-sm font-semibold text-ink">
          {t("bot.voice")}
        </label>{" "}
        <span className="text-xs text-muted">{t("bot.voiceHintServer")}</span>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <select
            id="bot-voice"
            value={chosen}
            disabled={saving}
            onChange={(e) => void choose(e.target.value as VoiceId)}
            className="min-w-0 flex-1 rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
          >
            {server.voices.map((v) => (
              <option key={v} value={v}>
                {t(v === "male" ? "bot.voiceMale" : "bot.voiceFemale")}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            onClick={() => void speakBest(sampleFor(lang), { lang, voice: chosen })}
          >
            {t("bot.voiceTest")}
          </Button>
        </div>
      </div>
    );
  }

  // --- fallback: this device's own voices --------------------------------
  if (browserVoices.length < 2) return null;

  const onPick = (uri: string) => {
    setLocalPick(uri);
    setPreferredVoice(uri || null);
  };

  return (
    <div className="mt-4">
      <label htmlFor="bot-voice" className="text-sm font-semibold text-ink">
        {t("bot.voice")}
      </label>{" "}
      <span className="text-xs text-muted">{t("bot.voiceHint")}</span>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <select
          id="bot-voice"
          value={localPick}
          onChange={(e) => onPick(e.target.value)}
          className="min-w-0 flex-1 rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
        >
          {/* The empty value is not "no voice" — it is "whichever this
              machine ranks best", which is the right default everywhere. */}
          <option value="">{t("bot.voiceAuto")}</option>
          {browserVoices.map((v) => (
            <option key={v.voiceURI} value={v.voiceURI}>
              {v.name}
            </option>
          ))}
        </select>
        <Button variant="secondary" onClick={() => speak(sampleFor(lang), { lang })}>
          {t("bot.voiceTest")}
        </Button>
      </div>
    </div>
  );
}
